// Service catalog used across the site.
// Each service has subpages with concise content blocks.

export const SERVICES = [
  {
    slug: "alta-nsps-surveys",
    name: "ALTA/NSPS Surveys",
    short: "Commercial-grade boundary + title surveys meeting 2021 ALTA/NSPS standards.",
    emoji: "📐",
    icon: "Ruler",
    color: "blue",
    hero: "We deliver Table-A compliant ALTA/NSPS Land Title Surveys for lenders, title companies, and commercial buyers across the US.",
    deliverables: [
      "Signed & sealed ALTA Survey (DWG + PDF)",
      "Table A items 1–20 as required",
      "Easement & encumbrance plotting",
      "Title commitment review",
    ],
    subpages: [
      { slug: "commercial-alta", name: "Commercial ALTA", body: "Multi-tenant retail, warehouse, hotel & mixed-use ALTA surveys with full Table-A compliance." },
      { slug: "lender-alta", name: "Lender / Refinance ALTA", body: "Lender-grade surveys for refinance and acquisition closings, delivered in 5–10 business days." },
      { slug: "title-review", name: "Title Commitment Review", body: "Schedule B-II exception plotting and written easement matrix." },
    ],
  },
  {
    slug: "boundary-surveys",
    name: "Boundary Surveys",
    short: "Legal boundary determination, monument recovery & parcel resolution.",
    emoji: "🧭",
    icon: "Compass",
    color: "green",
    hero: "Precise boundary line determination using historical deeds, monuments, and modern field data.",
    deliverables: [
      "Boundary Survey Map (DWG + PDF)",
      "Legal description (metes & bounds)",
      "Monument set/found schedule",
      "Adjoining deed analysis",
    ],
    subpages: [
      { slug: "residential-boundary", name: "Residential Boundary", body: "Lot retracement for fence, pool, and addition projects." },
      { slug: "commercial-boundary", name: "Commercial Boundary", body: "Multi-parcel commercial boundary resolution & easement plotting." },
      { slug: "rural-boundary", name: "Rural & Acreage", body: "Large-tract retracement using PLSS, GLO, and aerial control." },
    ],
  },
  {
    slug: "topographic-surveys",
    name: "Topographic Surveys",
    short: "1-foot contour mapping, spot elevations & DTM/DEM deliverables.",
    emoji: "⛰️",
    icon: "Mountain",
    color: "green",
    hero: "Detailed topographic mapping from total station, GNSS, drone, and LiDAR datasets — drafted to your CAD standards.",
    deliverables: [
      "Topographic Survey (DWG + PDF)",
      "Contours at 0.5' / 1' / 2' intervals",
      "Spot elevations & break-lines",
      "Existing utility tie-ins",
    ],
    subpages: [
      { slug: "site-topo", name: "Site Topographic", body: "Pre-design topo for site plan, grading, and drainage analysis." },
      { slug: "drone-topo", name: "Drone / UAV Topo", body: "Photogrammetric topographic drafting from your UAV deliverables." },
      { slug: "lidar-topo", name: "LiDAR Topo", body: "Point-cloud classification and topographic surface generation." },
    ],
  },
  {
    slug: "subdivision-plats",
    name: "Subdivision Plats",
    short: "Final plats, preliminary plats & re-plats for residential & commercial.",
    emoji: "🗺️",
    icon: "Map",
    color: "blue",
    hero: "From preliminary layouts to recorded final plats — engineered to local municipal standards.",
    deliverables: [
      "Preliminary & Final Plat sheets",
      "Lot & block addressing",
      "Easement & ROW dedications",
      "Municipal-ready submittal package",
    ],
    subpages: [
      { slug: "preliminary-plat", name: "Preliminary Plat", body: "Initial layout plats for planning commission review." },
      { slug: "final-plat", name: "Final Plat", body: "Recordable final plats with signatures, certificates, and seals." },
      { slug: "replat", name: "Re-Plat & Vacation", body: "Lot consolidations, splits, and easement vacations." },
    ],
  },
  {
    slug: "construction-staking",
    name: "Construction Staking",
    short: "Stake-out drawings, cut-sheets & coordinate files for the field.",
    emoji: "🚧",
    icon: "HardHat",
    color: "blue",
    hero: "Layout calculations and stake-out drawings that move from plan to field in hours, not days.",
    deliverables: [
      "Stake-out drawings (DWG + PDF)",
      "Cut-sheets (CSV / XLSX)",
      "Coordinate files (Trimble / Topcon / Leica)",
      "Layout calculations",
    ],
    subpages: [
      { slug: "building-staking", name: "Building Corner Staking", body: "Foundation corners, anchor bolts, and column lines." },
      { slug: "utility-staking", name: "Utility Staking", body: "Storm, sanitary, and water main stake-out cut-sheets." },
      { slug: "road-staking", name: "Road & Curb Staking", body: "Stationing, offsets, and curb-return geometry." },
    ],
  },
  {
    slug: "as-built-surveys",
    name: "As-Built Surveys",
    short: "Post-construction documentation for closeout & record drawings.",
    emoji: "🏗️",
    icon: "Building",
    color: "green",
    hero: "Accurate as-built drawings that document constructed conditions for owners, agencies, and record archives.",
    deliverables: [
      "As-Built Survey (DWG + PDF)",
      "Red-line comparison to design",
      "Utility invert & rim verification",
      "Certifications & seals",
    ],
    subpages: [
      { slug: "utility-as-built", name: "Utility As-Built", body: "Storm, sanitary, water, gas, and dry utility verification." },
      { slug: "building-as-built", name: "Building As-Built", body: "Floor plans, ceiling heights, and MEP point-cloud as-builts." },
      { slug: "site-as-built", name: "Site As-Built", body: "Final site grading, paving, and landscape as-built drafting." },
    ],
  },
  {
    slug: "floodplain-elevation-certificates",
    name: "Floodplain / Elevation Certificates",
    short: "FEMA Elevation Certificates & LOMA support drafting.",
    emoji: "🌊",
    icon: "Waves",
    color: "blue",
    hero: "FEMA-compliant Elevation Certificate drafting and LOMA / LOMR-F support packages.",
    deliverables: [
      "FEMA EC form (current version)",
      "Building photos & diagrams",
      "LOMA / LOMR-F application support",
      "Flood zone determination map",
    ],
    subpages: [
      { slug: "elevation-certificate", name: "Elevation Certificate", body: "Pre- and post-construction Elevation Certificate drafting." },
      { slug: "loma", name: "LOMA / LOMR-F", body: "Application packages to remove structures from the SFHA." },
    ],
  },
  {
    slug: "right-of-way",
    name: "Right-of-Way",
    short: "ROW plans, parcel maps & easement exhibits for DOT projects.",
    emoji: "🛣️",
    icon: "Route",
    color: "blue",
    hero: "Right-of-Way mapping, parcel acquisition exhibits, and easement drafting for DOT and municipal projects.",
    deliverables: [
      "ROW Plan & Profile",
      "Parcel acquisition exhibits",
      "Temporary & permanent easements",
      "Legal descriptions",
    ],
    subpages: [
      { slug: "dot-row", name: "DOT Right-of-Way", body: "State DOT-standard ROW plan sets and parcel exhibits." },
      { slug: "easement-exhibits", name: "Easement Exhibits", body: "Sealed easement exhibits with metes & bounds descriptions." },
    ],
  },
  {
    slug: "gis-mapping",
    name: "GIS Mapping",
    short: "Geo-referenced datasets, ESRI shapefiles & web-ready map tiles.",
    emoji: "🛰️",
    icon: "Satellite",
    color: "green",
    hero: "GIS data conversion, geo-referencing, and map production for utilities, planning, and asset management.",
    deliverables: [
      "ESRI shapefiles / geodatabases",
      "Geo-referenced raster mosaics",
      "Web-ready map tiles (MBTiles)",
      "Attribute schema design",
    ],
    subpages: [
      { slug: "utility-gis", name: "Utility GIS", body: "Water, sewer, and electric network geodatabases." },
      { slug: "parcel-gis", name: "Parcel GIS", body: "County-wide parcel fabric maintenance & QA." },
    ],
  },
  {
    slug: "site-plans",
    name: "Site Plans",
    short: "Pre-design site plans, layout plans & permit-ready drawings.",
    emoji: "🏙️",
    icon: "LayoutDashboard",
    color: "blue",
    hero: "Permit-ready site plans that combine boundary, topo, utilities, and proposed improvements on one sheet set.",
    deliverables: [
      "Site Plan (DWG + PDF)",
      "Existing & proposed utilities",
      "Grading & drainage overlay",
      "Zoning compliance notes",
    ],
    subpages: [
      { slug: "commercial-site-plan", name: "Commercial Site Plan", body: "Retail, office, and industrial site plan drafting." },
      { slug: "residential-site-plan", name: "Residential Site Plan", body: "Single-family and multi-family residential site plans." },
    ],
  },
  {
    slug: "3d-modeling",
    name: "3D Modeling",
    short: "Civil 3D corridors, surfaces, and BIM-ready 3D models.",
    emoji: "🧊",
    icon: "Box",
    color: "green",
    hero: "Civil 3D, Revit, and BIM-ready 3D models for design coordination, visualization, and machine-control.",
    deliverables: [
      "Civil 3D surfaces & corridors",
      "Revit / BIM models",
      "Machine-control models (LandXML)",
      "Renderings & walk-throughs",
    ],
    subpages: [
      { slug: "civil-3d", name: "Civil 3D Modeling", body: "Surface, corridor, and grading models in Autodesk Civil 3D." },
      { slug: "bim-revit", name: "BIM / Revit", body: "Architectural and MEP BIM models for coordination." },
      { slug: "machine-control", name: "Machine-Control Models", body: "LandXML and TRB models for GPS-guided grading equipment." },
    ],
  },
];

export const FLORIDA_COUNTIES = [
  { name: "Orange",       pincodes: ["32801", "32802", "32803", "32804", "32805"], seat: "Orlando" },
  { name: "Miami-Dade",   pincodes: ["33101", "33102", "33109", "33111", "33125"], seat: "Miami" },
  { name: "Broward",      pincodes: ["33301", "33302", "33303", "33304", "33309"], seat: "Fort Lauderdale" },
  { name: "Hillsborough", pincodes: ["33601", "33602", "33603", "33604", "33610"], seat: "Tampa" },
  { name: "Palm Beach",   pincodes: ["33401", "33402", "33403", "33404", "33405"], seat: "West Palm Beach" },
  { name: "Duval",        pincodes: ["32201", "32202", "32204", "32205", "32207"], seat: "Jacksonville" },
  { name: "Pinellas",     pincodes: ["33701", "33702", "33703", "33704", "33710"], seat: "St. Petersburg" },
  { name: "Lee",          pincodes: ["33901", "33902", "33903", "33904", "33907"], seat: "Fort Myers" },
];

export const REGIONS = [
  { code: "US", name: "United States", flag: "🇺🇸", tagline: "50 States · 3,143 Counties" },
  { code: "CA", name: "Canada",        flag: "🇨🇦", tagline: "10 Provinces · 3 Territories" },
  { code: "AU", name: "Australia",     flag: "🇦🇺", tagline: "6 States · 2 Territories" },
];

/* Mega-menu category groups — used by Navbar's Services mega-menu. */
export const SERVICE_CATEGORIES = [
  {
    title: "Boundary & Title",
    blurb: "Legal boundary, ALTA, plats & ROW drafting.",
    slugs: ["alta-nsps-surveys", "boundary-surveys", "subdivision-plats", "right-of-way"],
  },
  {
    title: "Topographic & Mapping",
    blurb: "Topo, GIS, floodplain & elevation drafting.",
    slugs: ["topographic-surveys", "gis-mapping", "floodplain-elevation-certificates"],
  },
  {
    title: "Construction & Modeling",
    blurb: "Stake-out, as-built, site plans & 3D models.",
    slugs: ["construction-staking", "as-built-surveys", "site-plans", "3d-modeling"],
  },
];
